'use client';

import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';
import { toast, Toaster as Sonner } from 'sonner';
import { X } from 'lucide-react';

import { RoundedWarningIcon } from '@/components/icons/rounded-warning';
import { SuccessRoundedIcon } from '@/components/icons/success-rounded';
import { InfoRoundedIcon } from '@/components/icons/info-rounded';

type ToasterProps = React.ComponentProps<typeof Sonner>;

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = 'system' } = useTheme();
  const [position, setPosition] = useState<'bottom-left' | 'top-center'>('bottom-left');

  useEffect(() => {
    const updatePosition = () => {
      if (typeof window !== 'undefined') {
        setPosition(window.innerWidth < 768 ? 'top-center' : 'bottom-left');
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, []);

  return (
    <Sonner
      theme={theme as ToasterProps['theme']}
      position={position}
      className="toaster group"
      toastOptions={{
        classNames: {
          toast:
            'group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg',
          description: 'group-[.toast]:text-muted-foreground',
          actionButton: 'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground',
          cancelButton: 'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground',
        },
      }}
      {...props}
    />
  );
};

const WARNING_TOAST_BASE_CLASSNAME = '!bg-white !border !border-[#FECACA] p-4';
const SUCCESS_TOAST_BASE_CLASSNAME = '!bg-white !border !border-[#E2E8F0] p-4';
const INFO_TOAST_BASE_CLASSNAME = '!bg-white !border !border-[#E2E8F0] p-4';

export function showWarningToast(message: string) {
  const toastId = toast.warning(
    <div className="flex items-start gap-3">
      <button
        type="button"
        className="absolute right-2 top-2 w-4 h-4 cursor-pointer hover:opacity-70 transition-opacity p-0 border-0 bg-transparent"
        onClick={() => toast.dismiss(toastId)}
      >
        <X className="w-full h-full [&>path]:!stroke-[#BEBEBE]" />
      </button>

      <RoundedWarningIcon className="flex-shrink-0" />
      <div className="flex flex-col gap-1">
        <h3 className="text-base font-medium text-[#DC2626]">Error</h3>
        <span style={{ color: '#DC2626' }}>{message}</span>
      </div>
    </div>,
    {
      className: WARNING_TOAST_BASE_CLASSNAME,
      style: { backgroundColor: '#FFFFFF' },
    },
  );
  return toastId;
}

export function showSuccessToast(message: string) {
  const toastId = toast.success(
    <div className="flex items-start gap-3">
      <button
        type="button"
        className="absolute right-2 top-2 w-4 h-4 cursor-pointer hover:opacity-70 transition-opacity p-0 border-0 bg-transparent"
        onClick={() => toast.dismiss(toastId)}
      >
        <X className="w-full h-full [&>path]:!stroke-[#BEBEBE]" />
      </button>
      <SuccessRoundedIcon className="flex-shrink-0" />
      <span className="text-base text-[#020617] font-medium">{message}</span>
    </div>,
    {
      className: SUCCESS_TOAST_BASE_CLASSNAME,
      style: { backgroundColor: '#FFFFFF' },
    },
  );
  return toastId;
}

export function showInfoToast(message: string, position: ToasterProps['position'] = 'top-center') {
  const toastId = toast.info(
    <div className="flex items-start gap-3">
      <button
        type="button"
        className="absolute right-2 top-2 w-4 h-4 cursor-pointer hover:opacity-70 transition-opacity p-0 border-0 bg-transparent"
        onClick={() => toast.dismiss(toastId)}
      >
        <X className="w-full h-full [&>path]:!stroke-[#BEBEBE]" />
      </button>

      <InfoRoundedIcon className="flex-shrink-0" />
      <span className="text-base text-[#020617] font-medium">{message}</span>
    </div>,
    {
      className: INFO_TOAST_BASE_CLASSNAME,
      style: { backgroundColor: '#FFFFFF' },
      position,
    },
  );
  return toastId;
}

export { Toaster };
